import { useState } from 'react';

export function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    roofType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('city', formData.city);
      formBody.append('roofType', formData.roofType);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5iop9hia7n1eqcqv3vg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          city: '',
          roofType: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact-section" className="py-16 sm:py-20 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-block bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-mail-send-line mr-1"></i>
            無料相談・お見積り
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            まずは無料診断から<br className="sm:hidden" />始めませんか？
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
            <strong>強引な営業は一切ありません。</strong><br className="hidden sm:block" />
            今の電気代がいくら安くなるか、補助金がいくら使えるか、<br className="hidden sm:block" />
            まずはシミュレーションしてみませんか？
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* お問い合わせフォーム */}
          <div className="bg-gray-50 rounded-2xl p-6 sm:p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-edit-box-line text-emerald-500 mr-2"></i>
              お問い合わせフォーム
            </h3>
            
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-sm"
                  placeholder="山田 太郎"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  電話番号 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-sm"
                  placeholder="090-1234-5678"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  メールアドレス
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-sm"
                  placeholder="example@email.com"
                />
              </div>

              <div>
                <label htmlFor="roofType" className="block text-sm font-medium text-gray-700 mb-1">
                  お問い合わせ項目
                </label>
                <select
                  id="roofType"
                  name="roofType"
                  value={formData.roofType}
                  onChange={(e) => setFormData({ ...formData, roofType: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-sm"
                >
                  <option value="">選択してください</option>
                  <option value="住宅用太陽光発電について">住宅用太陽光発電について</option>
                  <option value="蓄電池について">蓄電池について</option>
                  <option value="外壁リフォームについて">外壁リフォームについて</option>
                  <option value="エコキュート交換について">エコキュート交換について</option>
                  <option value="住宅リフォーム全般について">住宅リフォーム全般について</option>
                  <option value="その他お問い合わせ">その他お問い合わせ</option>
                </select>
              </div>

              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                  お住まいの市町村 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  required
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-sm"
                  placeholder="例：金沢市"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  ご質問・ご要望
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-sm resize-none"
                  placeholder="ご質問やご要望がありましたらご記入ください（500文字以内）"
                ></textarea>
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {formData.message.length}/500文字
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-4 rounded-lg font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    送信中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill mr-2"></i>
                    無料診断を申し込む
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg text-sm">
                  <i className="ri-check-line mr-2"></i>
                  送信が完了しました。担当者より折り返しご連絡いたします。
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  <i className="ri-error-warning-line mr-2"></i>
                  送信に失敗しました。お手数ですがお電話にてお問い合わせください。
                </div>
              )}
            </form>
          </div>

          {/* お電話でのお問い合わせ */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl p-6 sm:p-8 text-white">
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <i className="ri-phone-fill mr-2"></i>
                お電話でのお問い合わせ
              </h3>
              <p className="mb-6 opacity-95 leading-relaxed">
                お急ぎの方、直接相談したい方は<br />
                お気軽にお電話ください。
              </p>
              <a
                href="tel:0762553483"
                className="block bg-white text-emerald-600 text-center px-6 py-4 rounded-lg font-bold text-xl hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                <i className="ri-phone-fill mr-2"></i>
                076-255-3483
              </a>
              <p className="mt-4 text-sm opacity-90 text-center">
                受付時間：平日 9:00〜18:00
              </p>
            </div>

            {/* 安心ポイント */}
            <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100">
              <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-shield-check-line text-blue-600 mr-2"></i>
                安心してご相談ください
              </h4>
              <ul className="space-y-3">
                <li className="flex items-start space-x-2">
                  <i className="ri-check-line text-emerald-500 flex-shrink-0 mt-0.5"></i>
                  <span>押売りなど強引な営業はいたしません</span>
                </li>
                <li className="flex items-start space-x-2">
                  <i className="ri-check-line text-emerald-500 flex-shrink-0 mt-0.5"></i>
                  <span>お問い合わせ→現場調査→お見積もりの簡単3ステップ</span>
                </li>
              </ul>
            </div>

            {/* 営業時間・所在地 */}
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
              <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-information-line text-gray-600 mr-2"></i>
                会社情報
              </h4>
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex items-start space-x-3">
                  <i className="ri-building-line text-gray-500 flex-shrink-0 mt-0.5"></i>
                  <div>
                    <div className="font-medium">北陸クリーンエネルギー</div>
                    <div className="text-xs text-gray-600">Hokuriku Clean Energy</div>
                    <div className="text-xs text-gray-600">石川県知事許可（般-7）第20129号</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-map-pin-line text-gray-500 flex-shrink-0 mt-0.5"></i>
                  <span>〒920-8204 石川県金沢市戸水1-59</span>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-time-line text-gray-500 flex-shrink-0 mt-0.5"></i>
                  <span>10:00〜19:00　定休日：水曜日</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
